import { queryAll, showElement, hideElement } from './dom.js';

export function initAddOnPage() {
  const main = document.querySelector('[data-addon-main]');
  if (!main) return;

  const basePrice = parseFloat(main.dataset.basePrice) || 0;
  const priceEl   = document.querySelector('[data-addon-price]');
  const submitBtn = document.querySelector('[data-addon-submit]');
  const msgWidget = document.querySelector('[data-gift-card-message]');
  const msgField  = msgWidget?.querySelector('.gift-card-message__field');
  const errorEl   = document.querySelector('[data-gift-card-message-error]');

  // ── Footer price ─────────────────────────────────────────────────────────
  function calcTotal() {
    let extra = 0;
    queryAll('[data-add-on-group]').forEach(group => {
      queryAll('[data-add-on].is-selected', group).forEach(card => {
        const amountEl   = card.querySelector('.add-on__amount');
        const currencyEl = card.querySelector('.add-on__currency');
        if (!amountEl || !currencyEl) return; // "Free" cards have no currency span

        const amount = parseFloat(amountEl.textContent);
        if (isNaN(amount)) return;

        const qtyEl = card.querySelector('.add-on__qty-value');
        const qty   = qtyEl ? parseInt(qtyEl.textContent, 10) || 1 : 1;
        extra += amount * qty;
      });
    });
    return basePrice + extra;
  }

  function updateFooter() {
    if (!priceEl) return;
    priceEl.textContent = '£' + calcTotal().toFixed(2);
  }

  // Click events bubble from add-on buttons up to the group. The add-on
  // module's listeners (on children) fire first, so is-selected is already
  // updated by the time this handler runs.
  queryAll('[data-add-on-group]').forEach(group => {
    group.addEventListener('click', updateFooter);
  });

  // ── Submit ────────────────────────────────────────────────────────────────
  // Posts the selected add-ons/gift-card as a full page navigation (not
  // fetch/XHR — the backend responds with the next page). The endpoint is
  // read from the button's own data-addon-action rather than hardcoded, so
  // it can vary per deployment; csrf token and context path come from the
  // meta tags the backend integration adds to the page.
  function submitAddOns() {
    const action = submitBtn.dataset.addonAction;
    if (!action) return;

    const csrfToken   = document.querySelector('meta[name="_csrf"]')?.content;

    const payload = {
      productId:    document.getElementById('productId')?.value,
      quantity:     parseInt(document.getElementById('quantity')?.value, 10),
      shippingType: document.getElementById('shippingType')?.value,
      lineIdx:      document.getElementById('lineIdx')?.value,
      addons: [],
      giftCard: null,
    };

    queryAll('[data-add-on-group]').forEach(group => {
      const mode = group.dataset.mode || 'single';

      if (mode === 'single') {
        queryAll('article.is-selected[data-add-on]', group).forEach(card => {
          payload.addons.push({
            productId:  card.dataset.productId,
            quantity:   1,
            sortOrder:  group.dataset.sortOrder,
            urlSegment: group.dataset.urlSegment,
          });
        });
      } else if (mode === 'multiple') {
        queryAll('[data-add-on]', group).forEach(card => {
          const qtyControl = card.querySelector('.add-on__qty-control');
          if (!qtyControl || qtyControl.hidden) return; // skip if parent is hidden

          const quantity = +qtyControl.querySelector('.add-on__qty-value').textContent;
          if (quantity > 0) {
            payload.addons.push({
              productId:  card.dataset.productId,
              quantity,
              sortOrder:  group.dataset.sortOrder,
              urlSegment: group.dataset.urlSegment,
            });
          }
        });
      } else if (mode === 'gift-card') {
        queryAll('[data-add-on].is-selected', group).forEach(card => {
          payload.giftCard = {
            productId:  card.dataset.productId,
            quantity:   1,
            sortOrder:  group.dataset.sortOrder,
            urlSegment: group.dataset.urlSegment,
            message:    msgField ? msgField.value : null,
          };
        });
      }
    });

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = action;
    form.acceptCharset = "UTF-8";

    const csrfInput = document.createElement('input');
    csrfInput.type  = 'hidden';
    csrfInput.name  = '_csrf';
    csrfInput.value = csrfToken;
    form.appendChild(csrfInput);

    const dataInput = document.createElement('input');
    dataInput.type  = 'hidden';
    dataInput.name  = 'data';
    dataInput.value = JSON.stringify(payload);
    form.appendChild(dataInput);

    document.body.appendChild(form);
    form.submit();
  }

  // ── Basket button ─────────────────────────────────────────────────────────
  // Always enabled — validated on click rather than gated by disabling it,
  // so the visitor can attempt the action and be shown what's missing.
  // Reads minlength off the field itself (mirrors how maxlength already caps
  // typing) rather than duplicating the number here — msgField.minLength is
  // -1 when no minlength attribute is set, i.e. no minimum beyond non-empty.
  function messageErrorKind() {
    const value = msgField.value.trim();
    if (value === '') return 'empty';
    if (msgField.minLength > 0 && value.length < msgField.minLength) return 'short';
    return null;
  }

  function clearMessageError() {
    if (errorEl) hideElement(errorEl);
    if (msgField) msgField.setAttribute('aria-invalid', 'false');
  }

  function showMessageError(kind) {
    if (errorEl) {
      errorEl.textContent = kind === 'short' ? errorEl.dataset.shortMessage : errorEl.dataset.emptyMessage;
      showElement(errorEl);
    }
    if (msgField) msgField.setAttribute('aria-invalid', 'true');
  }

  if (submitBtn && msgWidget && msgField) {
    msgField.addEventListener('input', () => {
      const kind = messageErrorKind();
      if (!kind) {
        clearMessageError();
      } else if (errorEl && !errorEl.hidden) {
        // Error already showing from an earlier attempt — keep its wording
        // in sync as they type, rather than validating eagerly on every key.
        showMessageError(kind);
      }
    });

    submitBtn.addEventListener('click', () => {
      const kind = messageErrorKind();
      if (kind) {
        showMessageError(kind);

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        (msgWidget.closest('.addon-page__section') || msgWidget)
          .scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        msgField.focus({ preventScroll: true });
        return;
      }

      clearMessageError(); // defensive: clear any stale error from an earlier attempt
      submitAddOns();
    });
  }

  updateFooter();
}
